import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActiveDialog, LayoutModule } from '@monsido/angular-shared-components';
import { UploadMappingComponent } from './mapping/upload-mapping.component';
import { UploadValidationComponent } from './validation/upload-validation.component';
import { UploadProgressComponent } from './progress/upload-progress.component';
import { UploadResultsComponent } from './results/upload-results.component';

export type WizardStep = 'mapping' | 'validation' | 'progress' | 'results';

export interface AccessibilityCheckRow {
    id: number;
    name?: string;
    description?: string;
    friendly_name?: string;
    impact?: string;
    difficulty?: string;
    article_url?: string;
    rowNumber: number;
    errors?: string[];
}

export interface ImportResult {
    total: number;
    successful: number;
    failed: number;
    errors: Array<{ rowNumber: number; id: number; error: string }>;
    successfulUpdates: Array<{ rowNumber: number; id: number; name?: string }>;
}

@Component({
    selector: 'app-upload-accessibility-checks',
    imports: [
        CommonModule,
        LayoutModule,
        UploadMappingComponent,
        UploadValidationComponent,
        UploadProgressComponent,
        UploadResultsComponent,
    ],
    templateUrl: './upload-accessibility-checks.component.html',
    styleUrls: ['./upload-accessibility-checks.component.scss'],
})
export class UploadAccessibilityChecksComponent {
    @ViewChild(UploadMappingComponent) mappingComponent?: UploadMappingComponent;
    @ViewChild(UploadValidationComponent) validationComponent?: UploadValidationComponent;

    currentStep: WizardStep = 'mapping';
    parsedData: AccessibilityCheckRow[] = [];
    validatedData: AccessibilityCheckRow[] = [];
    importResult: ImportResult | null = null;

    constructor (
        public activeDialog: ActiveDialog,
    ) {}

    onMappingComplete (data: AccessibilityCheckRow[]): void {
        this.parsedData = data;
        this.currentStep = 'validation';
    }

    onValidationComplete (data: AccessibilityCheckRow[]): void {
        this.validatedData = data;
        this.currentStep = 'progress';
    }

    onProgressComplete (result: ImportResult): void {
        this.importResult = result;
        this.currentStep = 'results';
    }

    onBack (step: WizardStep): void {
        this.currentStep = step;
    }

    close (): void {
        this.activeDialog.close();
    }

    cancelDialog (): void {
        this.activeDialog.close();
    }

    getStepTitle (): string {
        switch (this.currentStep) {
            case 'mapping':
                return 'Import Accessibility Checks - Upload & Map CSV';
            case 'validation':
                return 'Import Accessibility Checks - Validate Data';
            case 'progress':
                return 'Import Accessibility Checks - Importing';
            case 'results':
                return 'Import Accessibility Checks - Results';
            default:
                return 'Import Accessibility Checks';
        }
    }

    canProceedFromMapping (): boolean {
        return this.mappingComponent?.canProceed() ?? false;
    }

    proceedFromMapping (): void {
        this.mappingComponent?.proceed();
    }

    canProceedFromValidation (): boolean {
        return this.validationComponent?.canProceed() ?? false;
    }

    getValidRowCount (): number {
        return this.validationComponent?.getValidRowCount() ?? 0;
    }

    proceedFromValidation (): void {
        this.validationComponent?.proceed();
    }
}
